import { useEffect, useRef, useState } from "react"
import { VertesiaClient } from "@vertesia/client"

export function useConfigurationCache(client: VertesiaClient) {
    const serviceRef = useRef<ConfigurationCacheService | null>(null)
    const [, forceUpdate] = useState({})

    if (!serviceRef.current) {
        serviceRef.current = new ConfigurationCacheService(client)
    }

    useEffect(() => {
        const service = serviceRef.current!

        const unsubscribe = service.subscribe(() => {
            forceUpdate({})
        })

        return unsubscribe
    }, [])

    return {
        getConfigurationDisplayName: (configurationIdentifier: string) => serviceRef.current!.getConfigurationDisplayName(configurationIdentifier),
        prefetchConfigurations: (configurationIdentifier: string[]) => serviceRef.current!.prefetchConfigurations(configurationIdentifier),
        isConfigurationFetching: (configurationIdentifier: string) => serviceRef.current!.isConfigurationFetching(configurationIdentifier),
        isConfigurationCached: (configurationIdentifier: string) => serviceRef.current!.isConfigurationCached(configurationIdentifier),
        getCachedConfigurationName: (configurationIdentifier: string) => serviceRef.current!.getCachedConfigurationName(configurationIdentifier),
        clearCache: () => serviceRef.current!.clearCache(),
        getCacheStats: () => serviceRef.current!.getCacheStats()
    }
}

export class ConfigurationCacheService {
    private configurationCache: Map<string, string> = new Map()
    private fetchingConfigurations: Set<string> = new Set()
    private listeners: Set<() => void> = new Set()
    private client: VertesiaClient

    constructor(client: VertesiaClient) {
        this.client = client
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => {
            this.listeners.delete(listener)
        }
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener())
    }

    getConfigurationDisplayName(configurationIdentifier: string): string {
        if (!configurationIdentifier) {
            return "Unknown Configuration"
        }

        if (this.configurationCache.has(configurationIdentifier)) {
            return this.configurationCache.get(configurationIdentifier)!
        }

        if (!this.fetchingConfigurations.has(configurationIdentifier)) {
            this.fetchConfigurationName(configurationIdentifier)
        }

        return "Loading..."
    }

    private async fetchConfigurationName(configurationIdentifier: string): Promise<void> {
        if (this.configurationCache.has(configurationIdentifier) || this.fetchingConfigurations.has(configurationIdentifier)) {
            return
        }

        this.fetchingConfigurations.add(configurationIdentifier)

        try {
            const config = await this.client.store.collections.retrieve(configurationIdentifier)
            const configName = config?.name || "Unknown Configuration"

            this.configurationCache.set(configurationIdentifier, configName)

            this.notifyListeners()
        } catch (error) {
            this.configurationCache.set(configurationIdentifier, "Unknown Configuration")
            this.notifyListeners()
        } finally {
            this.fetchingConfigurations.delete(configurationIdentifier)
        }
    }

    async prefetchConfigurations(configurationIdentifiers: string[]): Promise<void> {
        const uniqueConfigurations = new Set(configurationIdentifiers.filter(id => {
            const [type] = id.split(":")
            return type === "user" && !this.configurationCache.has(id) && !this.fetchingConfigurations.has(id)
        }))

        const fetchPromises = Array.from(uniqueConfigurations).map(configId => this.fetchConfigurationName(configId))
        await Promise.allSettled(fetchPromises)
    }

    isConfigurationFetching(configurationIdentifier: string): boolean {
        return this.fetchingConfigurations.has(configurationIdentifier)
    }

    isConfigurationCached(configurationIdentifier: string): boolean {
        return this.configurationCache.has(configurationIdentifier)
    }

    getCachedConfigurationName(configurationIdentifier: string): string | null {
        return this.configurationCache.get(configurationIdentifier) || null
    }

    clearCache(): void {
        this.configurationCache.clear()
        this.fetchingConfigurations.clear()
        this.notifyListeners()
    }

    getCacheStats(): {
        cachedConfigurations: number
        fetchingConfigurations: number
        totalRequests: number
    } {
        return {
            cachedConfigurations: this.configurationCache.size,
            fetchingConfigurations: this.fetchingConfigurations.size,
            totalRequests: this.configurationCache.size + this.fetchingConfigurations.size
        }
    }
}